import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { LAYOUT_NAMES } from "@/lib/boardShapes";
import { ago } from "@/lib/watch";
import NamePollResults from "@/components/NamePollResults";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

// Game stats for Jack (backend/admin_stats.py): players, games and trends.

const PEOPLE = "hsl(var(--brand))";
const BOTS = "hsl(var(--chart-4))";
const ENDINGS = { capture: "Flag captured", play: "Reached the base", resign: "Resigned", draw: "Agreed draw", sealed: "Sealed off (proximity)", other: "Other" };

const fmt = (n) => (n ?? 0).toLocaleString();
const shortDate = (d) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

function Tile({ label, value, sub }) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-display text-3xl font-bold mt-1 tabular-nums">{fmt(value)}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function Section({ title, note, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
      {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Split({ v }) {
  return (
    <span className="tabular-nums">
      <span className="font-semibold">{fmt(v.human + v.bot)}</span>
      <span className="text-muted-foreground text-xs ml-2">
        {fmt(v.human)} people · {fmt(v.bot)} bot
      </span>
    </span>
  );
}

const tooltipStyle = {
  contentStyle: {
    background: "hsl(var(--background))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 12,
    fontSize: 12,
  },
  labelFormatter: shortDate,
};

function Chart({ title, children }) {
  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="text-sm font-semibold mb-2">{title}</div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const axes = [
  <CartesianGrid key="g" stroke="hsl(var(--border))" strokeDasharray="3 3" vertical={false} />,
  <XAxis key="x" dataKey="date" tickFormatter={shortDate} stroke="hsl(var(--muted-foreground))" fontSize={11} minTickGap={24} />,
  <YAxis key="y" allowDecimals={false} stroke="hsl(var(--muted-foreground))" fontSize={11} width={32} />,
];

// Starting player win rate (Jack, 2026-10-17): how often whoever moved first won.
function StarterRate({ c, vsBot }) {
  if (!c || !c.games) return <span className="text-muted-foreground">No games</span>;
  return (
    <span className="tabular-nums">
      <span className="font-semibold">{Math.round((100 * c.starter_wins) / c.games)}%</span>
      <span className="text-muted-foreground text-xs ml-2">
        {fmt(c.starter_wins)} of {fmt(c.games)}
        {c.draws ? ` · ${fmt(c.draws)} drawn` : ""}
      </span>
      {vsBot && (
        <span className="block text-xs text-muted-foreground">
          People beat the bot {Math.round((100 * (c.person_wins || 0)) / c.games)}%
        </span>
      )}
    </span>
  );
}

const RULE_NAMES = { live: "Normal rules", unbroken: "Unbroken chain" };

// A row of pill buttons, one selected.
function Toggle({ label, options, value, onChange, testid }) {
  return (
    <div className="inline-flex rounded-full border border-border bg-surface p-1" role="group" aria-label={label} data-testid={testid}>
      {options.map(([v, text]) => (
        <button key={v} type="button" aria-pressed={value === v} onClick={() => onChange(v)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${value === v ? "bg-brand text-brand-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          {text}
        </button>
      ))}
    </div>
  );
}

// Retention (Jack, 2026-11-02): do players come back? From backend/retention_stats.py.
const pctText = (v) => (v == null ? "–" : `${v}%`);
const weekLabel = (w) => `${shortDate(w.start)} – ${shortDate(w.end)}`;

// A percentage cell shaded by how high it is, so good weeks stand out.
function PctCell({ v, ready }) {
  return (
    <td className="px-1.5 py-3 md:p-3 text-right tabular-nums" title={ready != null ? `${fmt(ready)} players old enough to count` : undefined}>
      <span className="inline-block min-w-[2.75rem] md:min-w-[3.25rem] px-1.5 md:px-2 py-0.5 rounded-md"
        style={v == null ? undefined : { background: `hsl(var(--brand) / ${0.08 + (0.6 * v) / 100})` }}>
        {pctText(v)}
      </span>
    </td>
  );
}

function Retention({ r }) {
  const a = r.active;
  const thisWeek = r.weeks[r.weeks.length - 1];
  const lastWeek = r.weeks[r.weeks.length - 2];
  const rows = [...r.cohorts.map((c) => ({ ...c, label: weekLabel(c) })), { ...r.cohorts_all, label: "All", total: true }];
  return (
    <Section
      title="Retention"
      note="Do players come back? Weeks are the last 7 days, the 7 before that, and so on. Active means signed up, signed in, played or finished a daily puzzle that day. Guests are left out except in the last line."
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-retention">
        <Tile label="Daily active" value={a.avg_daily_30} sub="Average day, last 30 days" />
        <Tile label="Weekly active" value={a.week} sub="Last 7 days" />
        <Tile label="Monthly active" value={a.month} sub="Last 30 days" />
        <Tile label="Stickiness" value={pctText(a.stickiness)} sub={`Daily ÷ monthly · weekly is ${pctText(a.week_of_month)} of monthly`} />
      </div>

      <div className="mt-3">
        <Chart title="Active players per week: new and returning">
          <BarChart data={r.weeks.map((w) => ({ ...w, date: w.start }))}>
            {axes}
            <Tooltip {...tooltipStyle} labelFormatter={(d) => weekLabel(r.weeks.find((w) => w.start === d) || { start: d, end: d })} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="returning" name="Returning" stackId="a" fill={PEOPLE} />
            <Bar dataKey="new" name="New this week" stackId="a" fill={BOTS} radius={[4, 4, 0, 0]} />
          </BarChart>
        </Chart>
      </div>

      <div className="mt-3 bg-surface border border-border rounded-2xl overflow-x-auto">
        <div className="px-4 pt-4 text-sm font-semibold">Players by sign-up week</div>
        <p className="px-4 text-xs text-muted-foreground mt-1">
          Of the players who signed up in the week starting that day, how many played a game, and how many came back on a later day, 7 or more days later, and 30 or more days later. A dash means nobody is old enough yet.
        </p>
        <table className="w-full text-sm mt-2">
          <thead className="text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="text-left font-medium px-1.5 py-3 md:p-3 pl-4">Signed up</th>
              <th className="text-right font-medium px-1.5 py-3 md:p-3">Players</th>
              <th className="text-right font-medium px-1.5 py-3 md:p-3">Played</th>
              <th className="text-right font-medium px-1.5 py-3 md:p-3">Came back</th>
              <th className="text-right font-medium px-1.5 py-3 md:p-3">7+ days</th>
              <th className="text-right font-medium px-1.5 py-3 md:p-3 pr-4">30+ days</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.label} className={`border-t border-border ${c.total ? "font-semibold" : ""}`}>
                <td className="px-1.5 py-3 md:p-3 pl-4 whitespace-nowrap">
                  {c.total ? c.label : <><span className="md:hidden">{shortDate(c.start)}</span><span className="hidden md:inline">{c.label}</span></>}
                </td>
                <td className="px-1.5 py-3 md:p-3 text-right tabular-nums">{fmt(c.signups)}</td>
                <PctCell v={c.played} />
                <PctCell v={c.back_1} ready={c.ready_1} />
                <PctCell v={c.back_7} ready={c.ready_7} />
                <PctCell v={c.back_30} ready={c.ready_30} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="mt-3 text-sm space-y-1 text-muted-foreground">
        <li>
          Games per active player: <span className="text-foreground font-semibold tabular-nums">{thisWeek.games_per_player ?? "–"}</span> this week
          {lastWeek && <> · {lastWeek.games_per_player ?? "–"} the week before</>} (people and bot games)
        </li>
        <li data-testid="stats-guest-conversion">
          Guests who made an account: <span className="text-foreground font-semibold tabular-nums">{pctText(r.guests.d30.rate)}</span>{" "}
          ({fmt(r.guests.d30.upgraded)} of {fmt(r.guests.d30.guests)}) in 30 days · {pctText(r.guests.all.rate)} ({fmt(r.guests.all.upgraded)} of {fmt(r.guests.all.guests)}) in all
        </li>
      </ul>
    </Section>
  );
}

export default function GameStats() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [error, setError] = useState(false);
  // Starting player win rates: 10-tile or 12-tile games (Jack, 2026-10-29).
  const [handTab, setHandTab] = useState("10");
  // ...and normal rules, the unbroken-chain test rule, or both (Jack, 2026-10-29).
  const [rulesTab, setRulesTab] = useState("live");

  useEffect(() => {
    const tz = -new Date().getTimezoneOffset();
    api.get("/admin/stats", { params: { tz_offset: tz } }).then(({ data }) => setS(data), () => setError(true));
  }, []);

  if (user && !user.feedback_reader) return <Navigate to="/lobby" replace />;

  const p = s?.players;
  const g = s?.games;
  const endingsTotal = s ? Object.values(s.endings_30).reduce((a, b) => a + b, 0) : 0;

  return (
    <main className="max-w-5xl mx-auto px-4 md:px-10 py-10" data-testid="game-stats">
      <h1 className="font-display text-3xl font-bold tracking-tight">Game stats</h1>
      <p className="text-muted-foreground mt-1">
        {s ? `How Jangle is doing, as of ${new Date(s.generated_at).toLocaleString()}` : error ? "" : "Loading…"}
      </p>
      {error && <p className="mt-6 text-sm text-red-500">Couldn't load the stats.</p>}

      <NamePollResults />

      {s && (
        <>
          <Section
            title="Players"
            note="Active means they played, finished a daily puzzle or were signed in that day. Sign-ins are counted from 15 Oct 2026; before that only playing counts."
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="Total players" value={p.total} />
              <Tile label="Online now" value={p.online_now} sub="Signed in, last 10 minutes" />
              <Tile label="Active today" value={p.active.today} sub={`${fmt(p.active.d7)} this week · ${fmt(p.active.d30)} in 30 days`} />
              <Tile label="New today" value={p.new.today} sub={`${fmt(p.new.d7)} this week · ${fmt(p.new.d30)} in 30 days`} />
            </div>
            {p.guests && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="stats-guests">
                Guests (not counted above): {fmt(p.guests.total)} in all · {fmt(p.guests.new.today)} today · {fmt(p.guests.new.d7)} this week · {fmt(p.guests.new.d30)} in 30 days
              </p>
            )}
          </Section>

          {s.retention && <Retention r={s.retention} />}

          <Section
            title="Current games"
            note="Games between people that moved in the last week, latest move first. Bot games aren't shown."
          >
            <div className="bg-surface border border-border rounded-2xl overflow-x-auto">
              {(s.current_games || []).length === 0 && (
                <p className="p-4 text-sm text-muted-foreground">No games between people going on right now.</p>
              )}
              <table className="w-full text-sm">
                {(s.current_games || []).length > 0 && (
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium p-3">Players</th>
                      <th className="text-left font-medium p-3">Board</th>
                      <th className="text-left font-medium p-3">Turn</th>
                      <th className="text-left font-medium p-3">Last move</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {(s.current_games || []).map((g) => (
                    <tr key={g.match_id} className="border-t border-border">
                      <td className="p-3 font-semibold whitespace-nowrap">
                        {[
                          [g.player1_id, g.player1_username],
                          [g.player2_id, g.player2_username],
                        ].map(([id, name], i) => (
                          <React.Fragment key={id || i}>
                            {i === 1 && <span className="text-muted-foreground font-normal"> v </span>}
                            <Link to={`/player/${id}`} className="hover:text-brand hover:underline underline-offset-2">
                              {name || "Unknown"}
                            </Link>
                          </React.Fragment>
                        ))}
                        <div className="text-xs font-normal text-muted-foreground">
                          {g.turn_user_id === g.player1_id
                            ? `${g.player1_username}'s turn`
                            : g.turn_user_id === g.player2_id
                              ? `${g.player2_username}'s turn`
                              : g.mode === "turn_based" ? "Untimed" : "Live"}
                        </div>
                      </td>
                      <td className="p-3 whitespace-nowrap">{LAYOUT_NAMES[g.layout] || g.layout}</td>
                      <td className="p-3 tabular-nums whitespace-nowrap">{g.turn} of 15</td>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">{ago(g.last_move_at)}</td>
                      <td className="p-3 text-right">
                        <Link to={`/watch/${g.match_id}`} state={{ back: true }} className="text-brand font-semibold hover:underline underline-offset-2">
                          Watch
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section
            title="Most active players"
            note="Ranked by how many different days they came back in the last 30 days (played, finished a daily puzzle or signed in)."
          >
            <div className="bg-surface border border-border rounded-2xl overflow-x-auto">
              {s.most_active_30.length === 0 && <p className="p-4 text-sm text-muted-foreground">Nobody yet.</p>}
              <table className="w-full text-sm">
                {s.most_active_30.length > 0 && (
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium p-3">Player</th>
                      <th className="text-right font-medium p-3">Days back</th>
                      <th className="text-right font-medium p-3">Games</th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {s.most_active_30.map((r) => (
                    <tr key={r.user_id} className="border-t border-border">
                      <td className="p-3 font-semibold truncate max-w-[10rem]">
                        {r.username || "Unknown"}
                        <div className="text-xs font-normal text-muted-foreground">Last here {shortDate(r.last_day)}</div>
                      </td>
                      <td className="p-3 text-right tabular-nums">
                        <span className="font-display text-xl font-bold">{fmt(r.days_30)}</span>
                        <div className="text-xs text-muted-foreground">{fmt(r.days_7)} this week</div>
                      </td>
                      <td className="p-3 text-right tabular-nums">
                        {fmt(r.games + r.bot_games)}
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {fmt(r.games)} people · {fmt(r.bot_games)} bot · {fmt(r.wins)} won
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Latest sign-ups" note="The 20 newest accounts. Tap a name to open their profile.">
            <div className="bg-surface border border-border rounded-2xl overflow-x-auto">
              {(s.latest_signups || []).length === 0 && <p className="p-4 text-sm text-muted-foreground">Nobody yet.</p>}
              <table className="w-full text-sm">
                {(s.latest_signups || []).length > 0 && (
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium p-3">Player</th>
                      <th className="text-left font-medium p-3">Signed up</th>
                      <th className="text-right font-medium p-3">Games</th>
                    </tr>
                  </thead>
                )}
                <tbody>
                  {(s.latest_signups || []).map((r) => (
                    <tr key={r.user_id} className="border-t border-border">
                      <td className="p-3 font-semibold truncate max-w-[10rem]">
                        <Link to={`/player/${r.user_id}`} className="hover:text-brand underline-offset-2 hover:underline">
                          {r.username || "Unknown"}
                        </Link>
                        {r.last_day && (
                          <div className="text-xs font-normal text-muted-foreground">Last here {shortDate(r.last_day)}</div>
                        )}
                      </td>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">
                        {new Date(r.created_at).toLocaleString(undefined, {
                          day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
                        })}
                      </td>
                      <td className="p-3 text-right tabular-nums">
                        {fmt(r.games + r.bot_games)}
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {fmt(r.games)} people · {fmt(r.bot_games)} bot
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Games" note="Bot games don't count towards Elo. Weeks and months are the last 7 and 30 days.">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Tile label="Games in progress" value={g.in_progress.human + g.in_progress.bot}
                sub={`${fmt(g.in_progress.human)} people · ${fmt(g.in_progress.bot)} bot`} />
              <Tile label="Finished today" value={g.finished.today.human + g.finished.today.bot}
                sub={`${fmt(g.finished.today.human)} people · ${fmt(g.finished.today.bot)} bot`} />
              <Tile label="Finished all time" value={g.finished.all.human + g.finished.all.bot}
                sub={`${fmt(g.finished.all.human)} people · ${fmt(g.finished.all.bot)} bot`} />
              <Tile label="Started all time" value={g.started.all.human + g.started.all.bot}
                sub={`${fmt(g.abandoned.human + g.abandoned.bot)} abandoned`} />
            </div>
            <div className="bg-surface border border-border rounded-2xl mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium p-3"></th>
                    <th className="text-left font-medium p-3">Today</th>
                    <th className="text-left font-medium p-3">7 days</th>
                    <th className="text-left font-medium p-3">30 days</th>
                  </tr>
                </thead>
                <tbody>
                  {[["Started", g.started], ["Finished", g.finished]].map(([label, row]) => (
                    <tr key={label} className="border-t border-border">
                      <td className="p-3 font-semibold">{label}</td>
                      <td className="p-3 whitespace-nowrap"><Split v={row.today} /></td>
                      <td className="p-3 whitespace-nowrap"><Split v={row.d7} /></td>
                      <td className="p-3 whitespace-nowrap"><Split v={row.d30} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(g.stale.human + g.stale.bot) > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {fmt(g.stale.human + g.stale.bot)} more games are still open but haven't moved for over a week, so
                they aren't counted as in progress.
              </p>
            )}
          </Section>

          {/* One section with a 10 tiles / 12 tiles toggle (Jack, 2026-10-29): every game went from 12 to 10 tiles. */}
          {(s.starting_player_by_hand
            ? [[handTab, (s.starting_player_by_hand_rules?.[rulesTab] || s.starting_player_by_hand)[handTab]]].filter(([, sp]) => sp)
            : s.starting_player ? [["all", s.starting_player]] : []).map(([n, sp]) => (
            <Section
              key={n}
              title="Starting player win rate, by board"
              note={n === "10"
                ? "Every game has used 10-tile hands since 29 Oct 2026 (Fortress 2.0 and Bridges always did). The percentage is how often the player who moved first went on to win. For games against bots it also shows how often the person won. Bot v bot test games aren't included."
                : n === "12"
                  ? "Games played with 12-tile hands, before 29 Oct 2026. The percentage is how often the player who moved first went on to win. For games against bots it also shows how often the person won. Bot v bot test games aren't included."
                  : "The percentage is how often the player who moved first went on to win, from every saved finished game. For games against bots it also shows how often the person won. Bot v bot test games aren't included."}
            >
              {s.starting_player_by_hand && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <Toggle label="Hand size" testid="hand-size-toggle" value={handTab} onChange={setHandTab}
                    options={[["10", "10 tiles"], ["12", "12 tiles"]]} />
                  {s.starting_player_by_hand_rules && (
                    <Toggle label="Rules" testid="rules-toggle" value={rulesTab} onChange={setRulesTab}
                      options={[["live", "Normal rules"], ["unbroken", "Unbroken chain"], ["all", "All rules"]]} />
                  )}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid={`starting-player-${n}`}>
                <Tile label="All games" value={sp.all.games
                  ? `${Math.round((100 * sp.all.starter_wins) / sp.all.games)}%` : "–"}
                  sub={`${fmt(sp.all.games)} games`} />
                <Tile label="People v people" value={sp.human.games
                  ? `${Math.round((100 * sp.human.starter_wins) / sp.human.games)}%` : "–"}
                  sub={`${fmt(sp.human.games)} games`} />
                <Tile label="People v bot" value={sp.bot.games
                  ? `${Math.round((100 * sp.bot.starter_wins) / sp.bot.games)}%` : "–"}
                  sub={`${fmt(sp.bot.games)} games`} />
              </div>
              <div className="bg-surface border border-border rounded-2xl mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium p-3"></th>
                      <th className="text-left font-medium p-3">People v people</th>
                      <th className="text-left font-medium p-3">People v bot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(sp.by_board).map(([layout, row]) => (
                      <tr key={layout} className="border-t border-border">
                        <td className="p-3 font-semibold whitespace-nowrap">{LAYOUT_NAMES[layout] || layout}</td>
                        <td className="p-3 whitespace-nowrap"><StarterRate c={row.human} /></td>
                        <td className="p-3 whitespace-nowrap"><StarterRate c={row.bot} vsBot /></td>
                      </tr>
                    ))}
                    {/* With the rules toggle the rows by rule set would repeat it. */}
                    {!s.starting_player_by_hand_rules && Object.entries(sp.by_rules).map(([rules, row]) => (
                      <tr key={rules} className="border-t border-border">
                        <td className="p-3 font-semibold whitespace-nowrap">{RULE_NAMES[rules] || rules}</td>
                        <td className="p-3 whitespace-nowrap"><StarterRate c={row.human} /></td>
                        <td className="p-3 whitespace-nowrap"><StarterRate c={row.bot} vsBot /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>
          ))}

          {s.letterheist_starting_player && (
            <Section
              title="Letterheist starting player win rate"
              note="How often the player who moved first won a finished Letterheist game. Against the Computer it also shows how often the person won."
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="lh-starting-player">
                {[["All games", s.letterheist_starting_player.all],
                  ["People v people", s.letterheist_starting_player.human],
                  ["People v computer", s.letterheist_starting_player.bot, true]].map(([label, c, vsBot]) => (
                  <Tile key={label} label={label}
                    value={c.games ? `${Math.round((100 * c.starter_wins) / c.games)}%` : "–"}
                    sub={`${fmt(c.games)} games${vsBot && c.games ? ` · person won ${Math.round((100 * c.person_wins) / c.games)}%` : ""}`} />
                ))}
              </div>
            </Section>
          )}

          <Section title="Last 30 days">
            <div className="grid md:grid-cols-2 gap-3">
              <Chart title="Active players per day">
                <LineChart data={s.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  {axes}
                  <Tooltip {...tooltipStyle} />
                  <Line type="monotone" dataKey="active" name="Active players" stroke={PEOPLE} strokeWidth={2} dot={false} />
                </LineChart>
              </Chart>
              <Chart title="Games started per day">
                <BarChart data={s.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  {axes}
                  <Tooltip {...tooltipStyle} cursor={{ fill: "hsl(var(--surface-hover))" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="human" name="People" stackId="g" fill={PEOPLE} />
                  <Bar dataKey="bot" name="Bot" stackId="g" fill={BOTS} radius={[3, 3, 0, 0]} />
                </BarChart>
              </Chart>
              <Chart title="New sign-ups per day">
                <BarChart data={s.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  {axes}
                  <Tooltip {...tooltipStyle} cursor={{ fill: "hsl(var(--surface-hover))" }} />
                  <Bar dataKey="signups" name="Sign-ups" fill={PEOPLE} radius={[3, 3, 0, 0]} />
                </BarChart>
              </Chart>
              <Chart title="Games finished and daily puzzles per day">
                <BarChart data={s.trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  {axes}
                  <Tooltip {...tooltipStyle} cursor={{ fill: "hsl(var(--surface-hover))" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="finished" name="Games finished" fill={PEOPLE} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="daily" name="Daily puzzles" fill={BOTS} radius={[3, 3, 0, 0]} />
                </BarChart>
              </Chart>
            </div>
          </Section>

          <div className="grid md:grid-cols-2 gap-x-6">
            <Section title="Boards">
              <div className="bg-surface border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="text-left font-medium p-3">Board</th>
                      <th className="text-right font-medium p-3">30 days</th>
                      <th className="text-right font-medium p-3">All time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.boards.map((b) => (
                      <tr key={b.layout} className="border-t border-border">
                        <td className="p-3 font-semibold">{LAYOUT_NAMES[b.layout] || b.layout}</td>
                        <td className="p-3 text-right tabular-nums">
                          {fmt(b.human_30 + b.bot_30)}
                          <div className="text-xs text-muted-foreground">{fmt(b.human_30)} people · {fmt(b.bot_30)} bot</div>
                        </td>
                        <td className="p-3 text-right tabular-nums">
                          {fmt(b.human + b.bot)}
                          <div className="text-xs text-muted-foreground">{fmt(b.human)} people · {fmt(b.bot)} bot</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title="How games against people ended" note="Finished games in the last 30 days.">
              <div className="bg-surface border border-border rounded-2xl p-4 flex flex-col gap-3">
                {endingsTotal === 0 && <p className="text-sm text-muted-foreground">No finished games yet.</p>}
                {Object.entries(s.endings_30)
                  .sort((a, b) => b[1] - a[1])
                  .map(([k, n]) => (
                    <div key={k}>
                      <div className="flex justify-between text-sm">
                        <span>{ENDINGS[k] || k}</span>
                        <span className="tabular-nums text-muted-foreground">
                          {fmt(n)} · {Math.round((100 * n) / endingsTotal)}%
                        </span>
                      </div>
                      <div className="h-2 rounded-full bg-border mt-1 overflow-hidden">
                        <div className="h-full rounded-full bg-brand" style={{ width: `${(100 * n) / endingsTotal}%` }} />
                      </div>
                    </div>
                  ))}
                {s.games.human_modes_30 && Object.keys(s.games.human_modes_30).length > 0 && (
                  <p className="text-xs text-muted-foreground border-t border-border pt-3">
                    Games against people started in 30 days: {fmt(s.games.human_modes_30.untimed)} untimed,{" "}
                    {fmt(s.games.human_modes_30.live)} live.
                  </p>
                )}
              </div>
            </Section>

            <Section title="Daily puzzle" note="Puzzles finished (solved or out of tries).">
              <div className="bg-surface border border-border rounded-2xl overflow-x-auto">
                <table className="w-full text-sm">
                  <tbody>
                    {[["Today", "today"], ["7 days", "d7"], ["30 days", "d30"]].map(([label, k]) => (
                      <tr key={k} className="border-t border-border first:border-t-0">
                        <td className="p-3 font-semibold">{label}</td>
                        <td className="p-3 text-right tabular-nums">{fmt(s.daily_puzzle[k].played)} played</td>
                        <td className="p-3 text-right tabular-nums">{fmt(s.daily_puzzle[k].solved)} solved</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

          </div>
        </>
      )}
    </main>
  );
}
